import { useEffect, useState, type FC } from 'react';
import { useDrag } from 'react-dnd';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css, useTheme } from '@mint-lib/styled';

import {
  Box,
  Icon,
  TableBodyCell,
  TableCellDraggable,
  TableHeaderCell,
  Tooltip,
  Typography,
} from '@myn/mui';

import {
  BUILDER_ROW_DEPTH,
  BUILDER_ROW_TYPES,
  DRAG_ROW_TYPE,
} from '../../../../../../constants/campaignBuilder.js';
import { Columns } from '../../../../../../constants/myCampaigns.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import NameInput from './NameInput.jsx';
import PlatformImage from './PlatformImage.jsx';
import PLatformLabel from './PLatformLabel.jsx';
import RowExpandButton from './RowExpandButton.jsx';

export type Props = {
  value: string;
  row: BuilderTableRow;
  column: BuilderTableColumn;
  isPanelOpened?: boolean;
};

type CellLevelDepth = 0 | 1 | 2;

const checkForRecommendationRows = (row: BuilderTableRow): boolean =>
  !!row?.subRows?.find(
    (tileRow: BuilderTableRow) => tileRow.original.isFromRecommendations,
  );

const NameField: FC<Props> = ({ value, row, column, isPanelOpened }) => {
  const { t } = useTranslation('@myn/planning');

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const theme = useTheme();

  const [isInitialOpened, setIsInitialOpened] = useState<boolean>(false);

  const {
    dateFrom,
    dateTo,
    budget,
    type,
    name,
    label,
    isBuyingPlanLocked,
    isPredictivePlanningMode,
    isArchivedVersion,
    platformByName,
    channelsMeta,
    lastRowOfChannelIds,
    scale,
  } = row.original;

  const isTileRow = type === BUILDER_ROW_TYPES.TILE;
  const isPlatformRow =
    type === BUILDER_ROW_TYPES.MEDIA_NETWORK ||
    type === BUILDER_ROW_TYPES.PLATFORM;
  const isChannelRowDepth = row.depth === BUILDER_ROW_DEPTH.CHANNEL;
  const isTotal = type === BUILDER_ROW_TYPES.TOTAL;
  const cellLevel =
    row.depth && column.id === Columns.EXPANDER
      ? (row.depth as CellLevelDepth)
      : undefined;
  const isReorderDisabled =
    !isAllowed || isPredictivePlanningMode || isBuyingPlanLocked;

  const isLastLevel = lastRowOfChannelIds.includes(row.id);

  const hasRecommendationRows =
    isPlatformRow && checkForRecommendationRows(row);

  const platform = name && platformByName ? platformByName[name] : null;

  const getIndicatorColor = () => {
    if (isArchivedVersion) {
      return theme.palette.text.disabled;
    }

    if (isRequiredFieldsFilled) {
      return theme.palette.primary.main;
    }

    return theme.palette.warning.main;
  };

  const [, drag] = useDrag({
    type: DRAG_ROW_TYPE,
    item: {
      ...row,
      type: DRAG_ROW_TYPE,
      isPanelOpened,
    },
    canDrag: () => !isPredictivePlanningMode && !isBuyingPlanLocked,
  });

  useEffect(() => {
    if (isInitialOpened && !column.isInitialOpened) {
      column.onAddClick(null);
      setIsInitialOpened(false);
      return;
    }
    if (column.isInitialOpened) {
      setIsInitialOpened(true);
    }
  }, [column.isInitialOpened]);

  const isRequiredFieldsFilled =
    dateFrom !== null && dateTo !== null && budget !== null;

  if (isTotal) {
    return (
      <StyledTotalsNameCell
        isPredictive={isPredictivePlanningMode}
        $scale={scale}
      >
        {label}
      </StyledTotalsNameCell>
    );
  }

  return (
    <Wrapper>
      <DragIconWrapper
        // @ts-expect-error wrong type in library
        ref={drag}
        $scale={scale}
        $isDisabled={isReorderDisabled}
      >
        <TableCellDraggable />
      </DragIconWrapper>

      {isTileRow ? (
        <TileRowWrapper>
          <RowExpandButton row={row} scale={scale} isHidden />
          <StyledTableBodyCell
            level={cellLevel}
            levelIsLast={isLastLevel}
            $isTileRow={isTileRow}
            $scale={scale}
          >
            <Wrapper>
              <Indicator $color={getIndicatorColor()} $scale={scale} />
              <NameInput row={row} column={column} value={value} />
            </Wrapper>
          </StyledTableBodyCell>
        </TileRowWrapper>
      ) : (
        <Wrapper>
          <RowExpandButton row={row} isHidden={!row.canExpand} scale={scale} />
          <StyledTableBodyCell
            level={cellLevel}
            levelIsLast={isLastLevel}
            $isTileRow={isTileRow}
            $scale={scale}
          >
            {isChannelRowDepth && name ? (
              <ChannelIcon
                component={channelsMeta[name]?.icon ?? 'Settings'}
                $scale={scale}
              />
            ) : (
              <PlatformImage row={row} scale={scale} logo={platform?.logo} />
            )}
            <Typography
              variant="bodyShort01"
              color={theme.palette.text.primary}
              fontSize={`${0.875 * scale}rem`}
              lineHeight={`${scale}rem`}
            >
              {isChannelRowDepth ? (
                label
              ) : (
                <PLatformLabel
                  label={platform?.label ?? ''}
                  width={Number(column.width)}
                  scale={scale}
                />
              )}
            </Typography>
            {hasRecommendationRows ? (
              <Tooltip
                title={t('New Recommended Platform. Please set it up.')}
                placement="top"
                arrow
              >
                <Box sx={{ display: 'flex' }}>
                  <Icon
                    component="Idea"
                    sx={{ width: 16 * scale, height: 16 * scale }}
                  />
                </Box>
              </Tooltip>
            ) : null}
          </StyledTableBodyCell>
        </Wrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const StyledTotalsNameCell = styled(TableHeaderCell)<{
  isPredictive: boolean;
  $scale: number;
}>`
  ${({ isPredictive, $scale, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
    padding-left: ${$scale < 1 ? 40 * $scale : 52}px;
    background-color: ${isPredictive
      ? theme.palette.background.primary
      : theme.palette.ui.secondary};
    font-size: ${0.875 * $scale}rem;
    overflow: hidden;
  `}
`;

const TileRowWrapper = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
`;

const StyledTableBodyCell = styled(TableBodyCell)<{
  level?: CellLevelDepth;
  $isTileRow: boolean;
  $scale: number;
}>`
  width: 100%;
  height: 100%;
  min-height: unset;
  & > div {
    min-height: unset;
  }
  ${({ $isTileRow }) => ($isTileRow ? 'padding-right: 0' : 'flex: 1')};

  & .table-cell-level {
    width: ${({ $scale, level }) => (level ? `${level * $scale}rem` : 'unset')};
  }
`;

const DragIconWrapper = styled(Box)<{
  $scale: number;
  $isDisabled: boolean;
}>`
  ${({ $scale, $isDisabled, theme }) => css`
    pointer-events: ${$isDisabled ? 'none' : 'all'};
    cursor: pointer;

    & > svg {
      transform: scale(${$scale});
    }

    ${css`
      height: 100%;
      flex: 0 0 ${20 * $scale}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-top: 1px solid ${theme.palette.ui.tertiary};

      & > td {
        scale: ${$scale};
        border-top: none;
      }
    `}
  `}
`;

const ChannelIcon = styled(Icon)<{ $scale: number }>`
  ${({ $scale }) => css`
    transform: scale(${$scale});
    margin-right: ${0.5 * $scale - 0.5}rem;
  `}
`;

const Indicator = styled(Box)<{
  $color: string;
  $scale: number;
}>`
  ${({ $color, $scale }) => `
    flex: 0 0 ${8 * $scale}px;
    width: ${8 * $scale}px;
    height: ${8 * $scale}px;
    border-radius: 50%;
    background-color: ${$color};
    margin-right: ${$scale > 1 ? 8 : 4}px;
  `}
`;

export default NameField;
